<script setup>
import { onMounted, onUnmounted, defineProps, ref } from 'vue'
import * as d3 from 'd3'
import * as topojson from "topojson-client"
import world_map from '@/assets/map/countries-110m.json'
import Countries from '@/assets/map/all.json'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
    required: false
  },
  height: {
    type: Number,
    default: 400
  }
})

var id2name = {}
let svg = null

// Color scale matching the provider theme colors
var colorScale = d3.scaleThreshold()
  .domain([1, 10, 50, 100, 500])
  .range([
    "#191F27",        // No data - dark background
    "#7357D240",      // 1+ - very light purple
    "#7357D260",      // 10+ - light purple
    "#7357D280",      // 50+ - medium purple
    "#7357D2A0",      // 100+ - darker purple
    "#7357D2"         // 500+ - full purple
  ])

function createMap(data) {
  d3.select('#provider-map').selectAll('*').remove()

  const containerWidth = document.getElementById('provider-map')?.clientWidth || 960
  const width = containerWidth
  const height = props.height

  // Create tooltip
  const tooltip = d3.select("#provider-map")
    .append("div")
    .style("opacity", 0)
    .attr("class", "map-tooltip")

  // Filter out Antarctica for projection fitting as well
  const features = topojson.feature(world_map, world_map.objects.countries).features
    .filter(d => d.id !== 10 && d.id !== "010")

  const projection = d3.geoNaturalEarth1()
    .fitSize([width, height], { type: "FeatureCollection", features: features })

  svg = d3.select("#provider-map").append("svg")
    .attr("id", "provider-svg")
    .attr("width", "100%")
    .attr("height", height)
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("preserveAspectRatio", "xMidYMid meet")
    .style("background-color", "transparent")

  const path = d3.geoPath().projection(projection)

  const mouseover = function(event, d) {
    const containerRect = document.getElementById('provider-map').getBoundingClientRect()
    const mouseX = event.clientX - containerRect.left
    const mouseY = event.clientY - containerRect.top

    tooltip
      .style("opacity", 1)
      .html(`
        <div class="country-name">${id2name[d.id] || 'Unknown'}</div>
        <div class="country-count">${d.total || 0} ip(s)</div>
      `)
      .style("left", Math.min(mouseX + 10, containerRect.width - 160) + "px")
      .style("top", Math.max(mouseY - 50, 10) + "px")

    d3.selectAll(".country-path")
      .transition()
      .duration(200)
      .style("opacity", 0.3)

    d3.select(this)
      .transition()
      .duration(200)
      .style("opacity", 1)
      .style("stroke", "#FFFFFF")
      .style("stroke-width", "1.5px")
  }

  const mouseleave = function(d) {
    tooltip.style("opacity", 0)

    d3.selectAll(".country-path")
      .transition()
      .duration(200)
      .style("opacity", 1)
      .style("stroke", "#374151")
      .style("stroke-width", "0.5px")
  }

  const g = svg.append("g")

  g.selectAll("path")
    .data(features)
    .enter()
    .append("path")
    .attr("d", path)
    .attr("class", "country-path")
    .attr("fill", function(d) {
      d.total = data[d.id] || 0
      return colorScale(d.total)
    })
    .style("stroke", "#374151")
    .style("stroke-width", "0.5px")
    .style("cursor", "pointer")
    .on("mouseover", mouseover)
    .on("mouseleave", mouseleave)
}

// Sample data for demonstration - replace with actual provider data
const sampleData = ref({
  840: 150,  // United States
  276: 85,   // Germany
  826: 75,   // United Kingdom
  124: 60,   // Canada
  250: 45,   // France
  392: 40,   // Japan
  156: 35,   // China
  36: 30,   // Australia
  528: 25,   // Netherlands
  752: 20    // Sweden
})

// Define handleResize in the correct scope
let handleResize = null

onMounted(async () => {
  // Build country mapping
  for (let i = 0; i < Countries.length; i++) {
    id2name[Countries[i]["country-code"]] = Countries[i].name
  }

  // Use provided data, sample data, or empty data for blank map
  let mapData = {}
  if (Object.keys(props.data).length > 0) {
    mapData = props.data
  } else {
    // Show sample data only if explicitly requested, otherwise show blank map
    mapData = {}
  }

  createMap(mapData)

  // Handle window resize
  handleResize = () => {
    setTimeout(() => {
      createMap(mapData)
    }, 100)
  }

  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (svg) {
    svg.remove()
  }
  if (handleResize) {
    window.removeEventListener('resize', handleResize)
  }
})
</script>

<template>
  <div class="provider-map-container">
    <div id="provider-map" class="map-content"></div>
  </div>
</template>

<style scoped>
.provider-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: transparent;
  overflow: hidden;
}

.map-content {
  width: 100%;
  height: 100%;
}

.no-data-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(15, 20, 25, 0.9);
  backdrop-filter: blur(2px);
}

.no-data-message {
  text-align: center;
  color: #6B7280;
  font-size: 0.9rem;
}

.no-data-message i {
  font-size: 2rem;
  color: #374151;
}

.text-muted {
  color: #4B5563;
  font-size: 0.8rem;
}

/* Tooltip styling */
:deep(.map-tooltip) {
  position: absolute;
  background-color: #1E252F;
  color: #FFFFFF;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #374151;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 10000;
  font-size: 0.85rem;
  max-width: 160px;
  min-width: 120px;
}

:deep(.country-name) {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

:deep(.country-count) {
  color: #7357D2;
  font-size: 0.8rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .provider-map-container {
    height: 300px;
  }

  .no-data-message {
    font-size: 0.8rem;
  }

  .no-data-message i {
    font-size: 1.5rem;
  }
}
</style>
